import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Alert,
  Checkbox,
  TablePagination,
  Switch,
  CircularProgress,
} from '@mui/material';
import { Edit, Refresh } from '@mui/icons-material';
import ToolbarButton from '../components/ToolbarButton';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';

const RoleManagement = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({
    RolesName: '',
    serviceid: '',
    Isactive: true,
  });
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchRoles();
      fetchServices();
    }
  }, [user]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/roles/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await res.json();
      setRoles(data);
    } catch (err) {
      console.error('Fetch roles error:', err);
      setError('Failed to fetch roles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      setLoadingServices(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/service-lookup`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch services');
      }

      const data = await res.json();
      setServices(data);
    } catch (err) {
      console.error('Fetch services error:', err);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setForm({
      RolesName: role.RolesName || '',
      serviceid: role.serviceid || '',
      Isactive: role.Isactive === 1 || role.Isactive === true,
    });
    setError('');
    setSuccess('');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingRole(null);
    setForm({
      RolesName: '',
      serviceid: '',
      Isactive: true,
    });
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!form.RolesName.trim()) {
      setError('Role name is required.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');

      const requestBody = {
        RolesName: form.RolesName.trim(),
        Isactive: form.Isactive,
      };

      // Include serviceid if selected, or set to null for global role
      if (form.serviceid && form.serviceid !== '') {
        requestBody.serviceid = parseInt(form.serviceid);
      } else {
        requestBody.serviceid = null;
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/service-management/roles/${editingRole.ID}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update role');
      }

      setSuccess('Role updated successfully!');
      setTimeout(() => {
        handleCloseEditDialog();
        fetchRoles();
      }, 1000);
    } catch (err) {
      console.error('Update role error:', err);
      setError(err.message || 'Failed to update role. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Filter roles for pagination
  const paginatedRoles = roles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (user?.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Access denied. Admin privileges required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <PageHeader title="Role Management" />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
          <ToolbarButton
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchRoles}
            disabled={loading}
            sx={{
              color: 'text.secondary',
              borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
              '&:hover': {
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
              },
            }}
          >
            Refresh
          </ToolbarButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 600 }}>
                Role Name
              </TableCell>
              <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 600 }}>
                Service
              </TableCell>
              <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 600 }}>
                Active
              </TableCell>
              <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 600 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedRoles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', color: 'text.secondary' }}>
                    No roles found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRoles.map((role) => (
                <TableRow key={role.ID} hover>
                  <TableCell sx={{ fontFamily: '"Inter", sans-serif' }}>
                    {role.RolesName}
                  </TableCell>
                  <TableCell sx={{ fontFamily: '"Inter", sans-serif' }}>
                    {role.ServiceName || 'Global'}
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={role.Isactive === 1 || role.Isactive === true}
                      disabled
                      sx={{ fontFamily: '"Inter", sans-serif' }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEdit(role)}
                      size="small"
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: (t) =>
                            t.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.04)'
                              : '#fafbfc',
                          color: 'text.primary',
                        },
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={roles.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          showFirstButton
          showLastButton
        />
      </TableContainer>

      {/* Edit Role Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={(event, reason) => {
          // Only close on explicit actions (Cancel button, etc.), not on backdrop click or Escape key
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseEditDialog();
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            fontFamily: '"Inter", sans-serif',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
          }}
        >
          Edit Role
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            variant="outlined"
            value={form.RolesName}
            onChange={(e) => setForm({ ...form, RolesName: e.target.value })}
            required
            sx={{
              mt: 2,
              '& .MuiInputBase-input': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
                fontWeight: 400,
                letterSpacing: '0.01em',
              },
              '& .MuiInputLabel-root': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
              },
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel
              id="edit-role-service-label"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
              }}
            >
              Service (Optional)
            </InputLabel>
            <Select
              labelId="edit-role-service-label"
              value={form.serviceid}
              label="Service (Optional)"
              onChange={(e) => setForm({ ...form, serviceid: e.target.value })}
              disabled={loadingServices}
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
                '& .MuiSelect-select': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9375rem',
                },
              }}
            >
              <MenuItem value="" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.9375rem' }}>
                <em>None (Global Role)</em>
              </MenuItem>
              {services.map((service) => (
                <MenuItem
                  key={service.Id}
                  value={service.Id}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.9375rem',
                  }}
                >
                  {service.Service}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={form.Isactive}
                onChange={(e) => setForm({ ...form, Isactive: e.target.checked })}
                sx={{ fontFamily: '"Inter", sans-serif' }}
              />
            }
            label="Active"
            sx={{
              mt: 2,
              '& .MuiFormControlLabel-label': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
                fontWeight: 400,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEditDialog}
            disabled={saving}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !form.RolesName.trim()}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '0.9375rem',
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default RoleManagement;

